import { Typography } from '@mui/material';
import FilterCohabitation from 'shell/components/shared/search-cohabitation/filter';
import { PmiButton } from 'shell/components/shared/button';
import { headCellsCohabitation } from 'cohabitation/constants/head-cells';
import { transformMembers } from 'cohabitation/utils/transform-members';
import { transformSearchCohabitationDataset } from 'cohabitation/utils/transform-search';
import { CohabitationVM } from 'cohabitation/view-models/list-cohabitation';
import { FiltersFormCohabitation } from 'cohabitation/view-models/filter-cohabitation';
import { useFormik } from 'formik';
import { Fragment, useCallback, useMemo, useState } from 'react';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import Table from 'shell/components/shared/table';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { HttpResponse } from 'shell/models/http-response';
import { FamilyDatas, ResponseAnprData } from 'shell/models/person';
import { noop } from 'shell/utils/noop';
import { ActionPropsCohabitation } from 'shell/view-models/actions-props';
import { ItemVM } from 'shell/view-models/item';
import { AnprVM } from 'shell/view-models/search-anpr';
import { createSearchParams, useNavigate } from 'react-router-dom';

interface CohabitationSearchProps {
  action: (props: ActionPropsCohabitation) => JSX.Element;
  goBackUrl?: string;
  destinationUrl?: string;
}
export default function CohabitationSearchPage({
  action: Action,
  goBackUrl,
  destinationUrl,
}: CohabitationSearchProps): React.ReactElement {
  const navigate = useNavigate();

  const [cohabitationSearch, getAnpr] = useHttpFetch<
    HttpResponse<ResponseAnprData>,
    AnprVM<CohabitationVM> & { datiFamigliaConvivenza?: FamilyDatas }
  >(`${__PERSON_API_URL__}/persona/convivenza/ricerca`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: [],
    },
    transformer: transformMembers,
  });

  const { userProfile } = useUserProfile();

  const data = cohabitationSearch.data as AnprVM<CohabitationVM> & {
    datiFamigliaConvivenza: FamilyDatas;
  };

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (data?.errors.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        data?.errors?.length > 1 ? `Ci sono ${data?.errors?.length} errori` : `C'è 1 errore`;
      errorToVisualize.messages = data?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, data]);

  const onSubmit = useCallback(
    (formValues: FiltersFormCohabitation) => {
      formikForm.setFieldValue('page', 0);

      const dataTransformed = transformSearchCohabitationDataset({ ...formValues, page: 0 });

      (
        getAnpr({
          data: dataTransformed,
        }) as ResponsePromise<
          HttpResponse<ResponseAnprData>,
          AnprVM<CohabitationVM> & { datiFamigliaConvivenza: FamilyDatas }
        >
      ).then(() => setVisibility({ error: undefined, warning: undefined }));
    },
    [getAnpr, setVisibility]
  );

  const formikForm = useFormik<FiltersFormCohabitation>({
    initialValues: {
      page: 0,
      rowsPerPage: 10,
      orders: {},
      municipalityOfResidence: {
        id: userProfile.datiLogin?.codiceIstat,
        description: userProfile.datiLogin?.nomeComune || '',
        province: userProfile.datiLogin?.siglaProvincia || '',
      },
      cardCode: 'Convivenza',
    },
    onSubmit,
  });

  const onChangePage = useCallback(
    (page) => {
      formikForm.setFieldValue('page', page || 0);
    },
    [formikForm.setFieldValue]
  );

  const onChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...formikForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      formikForm.setValues(newDataset);
    },
    [formikForm.setValues, formikForm.values]
  );

  const body: Array<CohabitationVM> = useMemo(() => {
    const formattedBody: Array<CohabitationVM> = (
      (data?.persons as Array<CohabitationVM>) || []
    ).map((person) => {
      return {
        ...person,
        action: (
          <Action cell={person} form={formikForm.values} goBackUrl={destinationUrl || goBackUrl} />
        ),
      };
    });

    return formattedBody;
  }, [data, Action]);

  const getSearchParams = useCallback(
    () =>
      createSearchParams({
        isNationalReasearch: formikForm.values?.isNationalResearch?.toString() || 'false',
        idFamigliaConvivenza:
          (
            cohabitationSearch.data as AnprVM<CohabitationVM> & {
              datiFamigliaConvivenza: FamilyDatas;
            }
          ).datiFamigliaConvivenza.famigliaConvivenza.idFamigliaConvivenzaComune
            ?.idFamigliaConvivenza || '',
        idFamigliaAnpr:
          (
            cohabitationSearch.data as AnprVM<CohabitationVM> & {
              datiFamigliaConvivenza: FamilyDatas;
            }
          ).datiFamigliaConvivenza.famigliaConvivenza.idFamigliaConvivenzaANPR || '',
      }),
    [formikForm.values?.isNationalResearch, cohabitationSearch.data]
  );

  return (
    <>
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Ricerca Convivenza
      </Typography>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={
          data?.warnings?.length > 1 ? `Ci sono ${data?.warnings?.length} warning` : `C'è 1 warning`
        }
        messages={data?.warnings}
        visibility={
          visibility.warning !== undefined ? visibility.warning : data?.warnings?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      <div className="pmi-my-3">
        <FilterCohabitation filterCohabitationForm={formikForm} goBackUrl={goBackUrl} />
      </div>
      {data ? (
        body.length > 0 ? (
          <Fragment>
            <Table
              ariaLabel="Lista convivenza"
              page={formikForm.values.page}
              rowsPerPage={formikForm.values.rowsPerPage}
              body={body}
              count={body?.length}
              head={headCellsCohabitation}
              onSort={noop}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              onVisibilityPress={noop}
              onEditPress={noop}
              orders={formikForm.values.orders}
              isNotLayoutFixed
              noSort
              hideButtons
            />

            <div className="pmi-pt-4 pmi-text-md-end">
              <div className="pmi-pr-md-4 pmi-pb-4 pmi-pb-md-0 pmi-d-md-inline-block pmi-d-block pmi-w-md-15">
                <PmiButton
                  variant="outlined"
                  color="cdmBrandSecondary"
                  fullWidth
                  disabled={!(body.length > 0)}
                  onClick={() =>
                    navigate({
                      pathname: 'storico',
                      search: `?${getSearchParams()}`,
                    })
                  }
                >
                  Storico
                </PmiButton>
              </div>
              <div className="pmi-pr-md-4 pmi-pb-4 pmi-pb-md-0 pmi-d-md-inline-block pmi-d-block pmi-w-md-15">
                <PmiButton
                  variant="outlined"
                  color="cdmBrandSecondary"
                  fullWidth
                  disabled={!(body.length > 0)}
                  onClick={() =>
                    navigate({
                      pathname: 'modifica-storico',
                      search: `?${getSearchParams()}`,
                    })
                  }
                >
                  Modifica Storico
                </PmiButton>
              </div>
            </div>
          </Fragment>
        ) : (
          <Typography className="pmi-pt-3" color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </>
  );
}

CohabitationSearchPage.displayName = 'home page component';
